import { detectOverflow } from '@floating-ui/react-dom';
import type {
  Boundary,
  Middleware,
  Padding,
  Placement,
} from '@floating-ui/react-dom';

interface FlipOnShowOptions {
  fallbackPlacement: Placement;
  padding?: Padding;
  boundary?: Boundary | null;
  isHidden?: boolean;
}

const CONTENT_VISIBLE_THRESHOLD = 5;
const MIDDLEWARE_NAME = 'flipOnShow';
const FINAL_PLACEMENT = 'finalPlacement';

/**
 * Middleware that flips the floating element to the opposite side if it
 * doesn't fit in the viewport on the initial show. After that, the element
 * won't flip anymore until next time it's hidden.
 */
const flipOnShow = (options: FlipOnShowOptions): Middleware => ({
  name: MIDDLEWARE_NAME,
  options,
  async fn(state) {
    if (
      options.isHidden ||
      state.elements.floating.clientHeight <= CONTENT_VISIBLE_THRESHOLD
    ) {
      // eslint-disable-next-line no-param-reassign
      delete state.elements.floating.dataset[FINAL_PLACEMENT];
      return {};
    }

    const finalPlacement: Placement = state.elements.floating.dataset[
      FINAL_PLACEMENT
    ] as Placement;

    // If final placement has been determined skip further computations
    if (finalPlacement && state.placement === finalPlacement) {
      return {};
    }

    if (finalPlacement && state.placement !== finalPlacement) {
      return { reset: { placement: finalPlacement } };
    }

    const isInitialPlacement = state.placement === state.initialPlacement;
    const isFallbackPlacement = state.placement === options.fallbackPlacement;

    const { top, bottom } = await detectOverflow(state, {
      ...options,
      boundary: options.boundary ?? undefined,
    });

    // Positive "overflow" means the popover doesn't fit in the viewport
    // The larger the value, the more it overflows
    const overflow = Math.max(top, bottom);

    // Popover doesn't fit in the initial placement, try the fallback placement
    if (isInitialPlacement && overflow > 0) {
      return {
        data: { initialPlacementOverflow: overflow },
        reset: { placement: options.fallbackPlacement },
      };
    }

    // Popover doesn't fit the fallback placement either and initial placement
    // is a better fit - reset to initial placement
    if (
      isFallbackPlacement &&
      overflow > state.middlewareData[MIDDLEWARE_NAME]?.initialPlacementOverflow
    ) {
      // eslint-disable-next-line no-param-reassign
      state.elements.floating.dataset[FINAL_PLACEMENT] = state.initialPlacement;
      return { reset: { placement: state.initialPlacement } };
    }

    // eslint-disable-next-line no-param-reassign
    state.elements.floating.dataset[FINAL_PLACEMENT] = state.placement;
    return {};
  },
});

export default flipOnShow;
